/* Ormonde Jayne — Search experience
   Client-side search over a Liquid-rendered product index.
   States: concierge (empty) → completions (typing) → results (best match /
   rows / format / corrected) → nothing close. Keyboard: Up/Down/Enter/Esc. */

(function () {
  'use strict';

  var root, overlay, input, clearBtn, helperEl, recentEl, listboxEl, conciergeEl, resultsEl;
  var CFG = null;
  var INDEX = [];
  var NOTE_VOCAB = null;
  var hlIndex = -1;
  var currentComps = [];
  var debounceTimer = null;
  var lastQuery = '';

  /* ---------- product index (lazy) ---------- */

  var indexPromise = null;

  function prepareIndex(list) {
    list.forEach(function (e) {
      e.nt = norm(e.t);
      e.nnotes = norm((e.notes || '') + ' ' + (e.style || ''));
    });
    return list;
  }

  /* Fetches CFG.indexUrl once per page, caching the raw JSON in sessionStorage
     for the rest of the visit. Re-runs the current query when it lands. */
  function loadIndex() {
    if (indexPromise) return indexPromise;
    if (!CFG || !CFG.indexUrl) { indexPromise = Promise.resolve(INDEX); return indexPromise; }
    var key = 'ojs-index:' + CFG.indexUrl;
    var cached = null;
    try { cached = sessionStorage.getItem(key); } catch (e) { /* private mode */ }
    if (cached) {
      try {
        INDEX = prepareIndex(JSON.parse(cached));
        NOTE_VOCAB = null;
        indexPromise = Promise.resolve(INDEX);
        return indexPromise;
      } catch (e) { /* fall through to a fresh fetch */ }
    }
    indexPromise = fetch(CFG.indexUrl, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        var list = JSON.parse(txt);
        try { sessionStorage.setItem(key, txt); } catch (e) { /* quota / private mode */ }
        INDEX = prepareIndex(list);
        NOTE_VOCAB = null;
        if (root && isOpen() && input && input.value) { lastQuery = ''; update(); }
        return INDEX;
      })
      .catch(function () { indexPromise = null; return INDEX; });
    return indexPromise;
  }

  /* ---------- utils ---------- */

  function norm(s) {
    if (!s) return '';
    var t = String(s).toLowerCase();
    try { t = t.normalize('NFD').replace(/[̀-ͯ]/g, ''); } catch (e) { /* older safari */ }
    t = t.replace(/[’'`´ʼ]/g, '');
    t = t.replace(/[^a-z0-9؀-ۿ]+/g, ' ');
    return t.replace(/\s+/g, ' ').trim();
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fmt(str, vars) {
    return String(str || '').replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] != null ? vars[k] : m;
    });
  }

  function lev(a, b) {
    if (a === b) return 0;
    var al = a.length, bl = b.length;
    if (!al) return bl;
    if (!bl) return al;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      cur[0] = i;
      for (j = 1; j <= bl; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      }
      var tmp = prev; prev = cur; cur = tmp;
    }
    return prev[bl];
  }

  function arrow() {
    return '<span class="ojs-arrow" aria-hidden="true">→</span>';
  }

  function priceLine(e) {
    if (!e.p) return '';
    var out = e.pv ? fmt(CFG.strings.from, { price: e.p }) : e.p;
    if (e.sc > 1) out += ' · ' + fmt(CFG.strings.sizes, { n: e.sc });
    return out;
  }

  function searchHref(q) {
    return CFG.searchUrl + '?q=' + encodeURIComponent(q);
  }

  /* ---------- recents ---------- */

  var RECENT_KEY = 'ojs:recent';

  function getRecents() {
    try {
      var v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(v) ? v.slice(0, 3) : [];
    } catch (e) { return []; }
  }

  function addRecent(q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    try {
      var v = getRecents().filter(function (x) { return norm(x) !== norm(q); });
      v.unshift(q);
      localStorage.setItem(RECENT_KEY, JSON.stringify(v.slice(0, 3)));
    } catch (e) { /* private mode */ }
  }

  function renderRecents() {
    var rec = getRecents();
    if (!rec.length || (input.value || '').trim()) {
      recentEl.hidden = true;
      recentEl.innerHTML = '';
      return;
    }
    var html = '<span class="ojs-recent-label">' + esc(CFG.strings.recent) + '</span>';
    rec.forEach(function (r) {
      html += '<button type="button" class="ojs-chip" data-ojs-recent-q="' + esc(r) + '">' + esc(r) + '</button>';
    });
    html += '<button type="button" class="ojs-recent-clear" data-ojs-recent-clear>' + esc(CFG.strings.clear) + '</button>';
    recentEl.innerHTML = html;
    recentEl.hidden = false;
  }

  /* ---------- matching ---------- */

  function noteVocab() {
    if (NOTE_VOCAB) return NOTE_VOCAB;
    NOTE_VOCAB = {};
    INDEX.forEach(function (e) {
      norm((e.notes || '') + ' ' + (e.style || '')).split(' ').forEach(function (w) {
        if (w.length > 3) NOTE_VOCAB[w] = true;
      });
    });
    return NOTE_VOCAB;
  }

  function wordPrefixMatch(hay, q) {
    /* true if any word in hay starts with q */
    if (!hay) return false;
    if (hay.indexOf(q) === 0) return true;
    return hay.indexOf(' ' + q) !== -1;
  }

  function nameScore(e, q) {
    var t = e.nt;
    if (t === q) return 100;
    if (t.indexOf(q) === 0) return 80;
    if (wordPrefixMatch(t, q)) return 60;
    if (q.length >= 4 && t.indexOf(q) !== -1) return 40;
    return 0;
  }

  function noteMatch(e, q) {
    if (q.length < 4) return false;
    return wordPrefixMatch(e.nnotes, q);
  }

  function isFormatQuery(q) {
    var kws = CFG.formatKeywords || [];
    for (var i = 0; i < kws.length; i++) {
      if (q.indexOf(kws[i]) === 0 || kws[i].indexOf(q) === 0 && q.length >= 4) return true;
    }
    return false;
  }

  function compute(qRaw) {
    var q = norm(CFG.aliases && CFG.aliases[norm(qRaw)] ? CFG.aliases[norm(qRaw)] : qRaw);
    if (!q) return { kind: 'concierge' };

    /* format intent — "sample", "try", "tasting"… */
    if (isFormatQuery(q)) {
      var menus = INDEX.filter(function (e) { return e.type === 'm'; });
      if (menus.length) return { kind: 'format', items: menus.slice(0, 4), q: qRaw };
    }

    var named = [], noted = [];
    INDEX.forEach(function (e) {
      var s = nameScore(e, q);
      if (s > 0) { e._s = s; named.push(e); }
      else if (noteMatch(e, q)) noted.push(e);
    });
    named.sort(function (a, b) { return b._s - a._s || a.t.localeCompare(b.t); });

    /* collection / mood direct match */
    var collDirect = null;
    (CFG.lists || []).forEach(function (c) {
      if (collDirect) return;
      var n = norm(c.name);
      if (n === q || (q.length >= 4 && n.indexOf(q) === 0)) collDirect = c;
    });

    var isNote = q.length >= 4 && noteVocab()[q] === true;
    var frags = named.filter(function (e) { return e.type === 'f'; });
    var homes = named.filter(function (e) { return e.type === 'h'; });
    var menusHit = named.filter(function (e) { return e.type === 'm'; });

    if (isNote) {
      /* merge note matches into the fragrance row, intent over keyword */
      noted.forEach(function (e) {
        if (e.type === 'f' && frags.indexOf(e) === -1) frags.push(e);
        if (e.type === 'h' && homes.indexOf(e) === -1) homes.push(e);
      });
    }

    /* collection-mode: query names a collection or mood, nothing named */
    if (!frags.length && !homes.length && collDirect) {
      var members = INDEX.filter(function (e) {
        return e.type === 'f' && (e.coll === collDirect.name || (e.moods || []).indexOf(collDirect.handle) !== -1);
      });
      if (members.length) {
        return {
          kind: 'rows', q: qRaw,
          rowLabel: collDirect.name, rowLink: collDirect.url,
          frags: members.slice(0, 5), homes: [], menus: [],
          also: [{ name: collDirect.name, url: collDirect.url, kind: collDirect.kind }],
          total: members.length
        };
      }
    }

    if (!frags.length && !homes.length && !menusHit.length && noted.length) {
      /* pure note query outside vocab word-boundaries */
      noted.forEach(function (e) {
        if (e.type === 'f') frags.push(e);
        else if (e.type === 'h') homes.push(e);
      });
      isNote = true;
    }

    if (!frags.length && !homes.length && !menusHit.length) {
      /* fuzzy — understood, not corrected */
      var bestTerm = null, bestD = 3;
      INDEX.forEach(function (e) {
        var d = lev(e.nt, q);
        var cap = e.nt.length <= 4 ? 1 : 2;
        if (d <= cap && d < bestD) { bestD = d; bestTerm = e.t; }
        /* first word too — "montobacco" → "montabaco …" */
        var w = e.nt.split(' ')[0];
        d = lev(w, q);
        cap = w.length <= 4 ? 1 : 2;
        if (d <= cap && d < bestD) { bestD = d; bestTerm = e.t.split(' ')[0]; }
      });
      if (bestTerm) {
        var inner = compute(bestTerm);
        if (inner.kind !== 'none' && inner.kind !== 'concierge') {
          inner.correctedFrom = qRaw;
          inner.correctedTo = bestTerm;
          return inner;
        }
      }
      return { kind: 'none', q: qRaw };
    }

    var total = frags.length + homes.length + menusHit.length;

    /* best match: a single dominant fragrance */
    var best = null;
    if (frags.length && (frags[0].nt === q || frags.length === 1) && frags[0]._s >= 60) {
      if (frags.length === 1 || frags[0].nt === q) best = frags[0];
    }
    if (best && frags.length <= 2) {
      var also = [];
      if (best.coll && best.collUrl) also.push({ name: best.coll, url: best.collUrl, kind: CFG.strings.kindCollection });
      (best.moods || []).slice(0, 1).forEach(function (m) {
        var mm = findList(m);
        if (mm) also.push({ name: mm.name, url: mm.url, kind: CFG.strings.kindMood });
      });
      return { kind: 'best', q: qRaw, item: best, also: also, homes: homes.slice(0, 3), total: total };
    }

    /* family label: most name-hits share one line collection */
    var rowLabel = CFG.strings.fragrances, rowLink = null;
    if (isNote) {
      rowLabel = fmt(CFG.strings.fragrancesWith, { q: qRaw.trim() });
    } else if (frags.length >= 3) {
      var counts = {};
      frags.forEach(function (e) { if (e.coll) counts[e.coll] = (counts[e.coll] || 0) + 1; });
      var top = null;
      Object.keys(counts).forEach(function (k) { if (!top || counts[k] > counts[top]) top = k; });
      if (top && counts[top] >= 3 && counts[top] >= frags.length * 0.6) {
        rowLabel = top;
        var first = frags.filter(function (e) { return e.coll === top; })[0];
        rowLink = first ? first.collUrl : null;
        frags = frags.filter(function (e) { return e.coll === top; }).concat(
          frags.filter(function (e) { return e.coll !== top; })
        );
      }
    }

    /* Also links: collections + moods of the matches, plus direct hits */
    var also = [], seen = {};
    function pushAlso(name, url, kind) {
      if (!name || !url || seen[name]) return;
      seen[name] = true;
      also.push({ name: name, url: url, kind: kind });
    }
    if (collDirect) pushAlso(collDirect.name, collDirect.url, collDirect.kind);
    frags.slice(0, 5).forEach(function (e) {
      if (e.coll !== rowLabel) pushAlso(e.coll, e.collUrl, CFG.strings.kindCollection);
    });
    var moodCounts = {};
    frags.slice(0, 5).forEach(function (e) {
      (e.moods || []).forEach(function (m) { moodCounts[m] = (moodCounts[m] || 0) + 1; });
    });
    var topMood = null;
    Object.keys(moodCounts).forEach(function (k) { if (!topMood || moodCounts[k] > moodCounts[topMood]) topMood = k; });
    if (topMood) {
      var ml = findList(topMood);
      if (ml) pushAlso(ml.name, ml.url, CFG.strings.kindMood);
    }

    return {
      kind: 'rows', q: qRaw,
      rowLabel: rowLabel, rowLink: rowLink,
      frags: frags.slice(0, 5), homes: homes.slice(0, 3), menus: menusHit.slice(0, 4),
      also: also.slice(0, 4), total: total
    };
  }

  function findList(handleOrName) {
    var lists = CFG.lists || [];
    for (var i = 0; i < lists.length; i++) {
      if (lists[i].handle === handleOrName || norm(lists[i].name) === norm(handleOrName)) return lists[i];
    }
    return null;
  }

  /* ---------- completions ---------- */

  function computeCompletions(qRaw) {
    var q = norm(qRaw);
    if (!q) return [];
    var out = [];
    INDEX.forEach(function (e) {
      if (e.nt === q) return; /* nothing left to complete */
      var s = 0;
      if (e.nt.indexOf(q) === 0) s = 3;
      else if (wordPrefixMatch(e.nt, q)) s = 2;
      if (s) out.push({ e: e, s: s + (e.type === 'f' ? 0.5 : 0) });
    });
    out.sort(function (a, b) { return b.s - a.s || a.e.t.localeCompare(b.e.t); });
    return out.slice(0, 6).map(function (x) {
      var e = x.e, pre = '', rest = e.t;
      if (e.nt.indexOf(q) === 0) {
        /* split the display title at the typed length, tolerant of punctuation */
        var cut = 0, seen = 0;
        var target = q.replace(/ /g, '').length;
        while (cut < e.t.length && seen < target) {
          if (/[a-z0-9؀-ۿ]/i.test(e.t.charAt(cut))) seen++;
          cut++;
        }
        pre = e.t.slice(0, cut);
        rest = e.t.slice(cut);
      }
      return { e: e, pre: pre, rest: rest };
    });
  }

  function renderCompletions(comps) {
    currentComps = comps;
    hlIndex = comps.length ? 0 : -1;
    if (!comps.length) {
      listboxEl.hidden = true;
      listboxEl.innerHTML = '';
      input.removeAttribute('aria-activedescendant');
      return;
    }
    var html = '';
    comps.forEach(function (c, i) {
      var e = c.e;
      html += '<a href="' + esc(e.u) + '" role="option" id="ojs-opt-' + i + '"' +
        (i === hlIndex ? ' aria-selected="true"' : '') +
        ' class="ojs-comp' + (i === hlIndex ? ' is-hl' : '') + '" data-ojs-comp="' + i + '">' +
        '<span class="ojs-comp-thumb">' + (e.img ? '<img src="' + esc(e.img) + '" alt="" loading="lazy">' : '') + '</span>' +
        '<span class="ojs-comp-text">' +
        (c.pre ? esc(c.pre) + '<span class="rest">' + esc(c.rest) + '</span>' : esc(e.t)) +
        '</span>' +
        '<span class="ojs-comp-arrow" aria-hidden="true">→</span>' +
        '</a>';
    });
    listboxEl.innerHTML = html;
    listboxEl.hidden = false;
    input.setAttribute('aria-activedescendant', 'ojs-opt-0');
  }

  function moveHl(delta) {
    if (!currentComps.length) return;
    hlIndex = (hlIndex + delta + currentComps.length) % currentComps.length;
    var opts = listboxEl.querySelectorAll('.ojs-comp');
    for (var i = 0; i < opts.length; i++) {
      opts[i].classList.toggle('is-hl', i === hlIndex);
      if (i === hlIndex) opts[i].setAttribute('aria-selected', 'true');
      else opts[i].removeAttribute('aria-selected');
    }
    input.setAttribute('aria-activedescendant', 'ojs-opt-' + hlIndex);
  }

  /* ---------- rendering ---------- */

  function cardHTML(e) {
    return '<a href="' + esc(e.u) + '" class="ojs-card">' +
      '<span class="ojs-card-img">' +
      (e.img ? '<img src="' + esc(e.img) + '" alt="" loading="lazy">' : '') +
      (e.badge ? '<span class="ojs-badge">' + esc(e.badge) + '</span>' : '') +
      '</span>' +
      '<span class="ojs-card-name">' + esc(e.t) + '</span>' +
      (e.prof ? '<span class="ojs-card-profile">' + esc(e.prof) + '</span>' : '') +
      '<span class="ojs-card-price">' + esc(priceLine(e)) + '</span>' +
      '</a>';
  }

  function homeCardHTML(e) {
    return '<a href="' + esc(e.u) + '" class="ojs-home-card">' +
      '<span class="ojs-home-img">' + (e.img ? '<img src="' + esc(e.img) + '" alt="" loading="lazy">' : '') + '</span>' +
      '<span><span class="ojs-home-name">' + esc(e.t) + '</span>' +
      '<span class="ojs-home-price">' + esc(priceLine(e)) + '</span></span>' +
      '</a>';
  }

  function alsoHTML(also) {
    if (!also || !also.length) return '';
    var html = '<div class="ojs-band"><span class="ojs-label">' + esc(CFG.strings.also) + '</span><div class="ojs-also-list">';
    also.forEach(function (a) {
      html += '<a href="' + esc(a.url) + '" class="ojs-also-link">' + esc(a.name) +
        ' <span class="ojs-also-kind">' + esc(a.kind) + '</span></a>';
    });
    return html + '</div></div>';
  }

  function seeAllHTML(model) {
    if (!model.total || model.total < 1) return '';
    return '<div class="ojs-seeall-row"><a href="' + esc(searchHref(model.q)) + '" class="ojs-seeall">' +
      esc(fmt(CFG.strings.seeAll, { n: model.total })) + ' ' + arrow() + '</a></div>';
  }

  function correctionHTML(model) {
    if (!model.correctedFrom) return '';
    return '<div class="ojs-correction">' +
      '<span class="ojs-correction-text">' + esc(CFG.strings.showingFor) + ' <strong>' + esc(model.correctedTo) + '</strong></span>' +
      '<a class="ojs-correction-instead" href="' + esc(searchHref(model.correctedFrom)) + '">' +
      esc(fmt(CFG.strings.searchInstead, { q: model.correctedFrom })) + '</a>' +
      '</div>';
  }

  function bestHTML(model) {
    var e = model.item;
    var html = correctionHTML(model);
    html += '<div class="ojs-best">' +
      '<div class="ojs-best-img">' + (e.img ? '<img src="' + esc(e.img) + '" alt="' + esc(e.t) + '">' : '') + '</div>' +
      '<div class="ojs-best-body">' +
      '<span class="ojs-label">' + esc(e.avail ? CFG.strings.bestMatch : (e.coll || CFG.strings.bestMatch)) + '</span>' +
      '<h3 class="ojs-best-name">' + esc(e.t) + '</h3>' +
      (e.avail && e.coll ? '<span class="ojs-best-coll">' + esc(e.coll) + '</span>' : '') +
      (e.prof ? '<span class="ojs-best-profile">' + esc(e.prof) + '</span>' : '') +
      (e.notes ? '<span class="ojs-best-notes">' + esc(e.notes) + '</span>' : '') +
      '<span class="ojs-best-price">' + esc(priceLine(e)) + '</span>';

    if (e.avail) {
      html += '<div class="ojs-best-actions">' +
        '<a href="' + esc(e.u) + '" class="ojs-btn">' + esc(CFG.strings.view) + '</a>' +
        (e.v10 ? '<a href="' + esc(e.u) + '?variant=' + esc(e.v10) + '" class="ojs-textlink">' + esc(e.v10d ? (CFG.strings.try2x10 || CFG.strings.try10) : CFG.strings.try10) + '</a>'
               : '') +
        '</div>';
    } else {
      html += '<span class="ojs-best-note">' + esc(fmt(CFG.strings.notAvailable, { name: e.t })) + '</span>' +
        '<div class="ojs-best-actions">' +
        '<a href="' + esc(e.u) + '" class="ojs-btn fill">' + esc(CFG.strings.registerInterest) + '</a>' +
        (CFG.boutiqueUrl ? '<a href="' + esc(CFG.boutiqueUrl) + '" class="ojs-btn">' + esc(CFG.strings.visitBoutique) + '</a>' : '') +
        '</div>';
    }
    html += '</div></div>';

    if (model.homes && model.homes.length) {
      html += '<div class="ojs-band"><span class="ojs-label">' + esc(CFG.strings.home) + '</span>' +
        '<div class="ojs-home-row">' + model.homes.map(homeCardHTML).join('') + '</div></div>';
    }
    html += alsoHTML(model.also);
    if (model.total > 1) html += seeAllHTML(model);
    return html;
  }

  function rowsHTML(model) {
    var html = correctionHTML(model);
    if (model.frags.length) {
      html += '<div class="ojs-band">' +
        '<div class="ojs-band-head"><span class="ojs-label">' + esc(model.rowLabel) + '</span>' +
        (model.rowLink ? '<a href="' + esc(model.rowLink) + '" class="ojs-rowlink">' + esc(CFG.strings.seeSeries) + ' ' + arrow() + '</a>' : '') +
        '</div>' +
        '<div class="' + (model.frags.length >= 5 ? 'ojs-cards-5' : 'ojs-cards-4') + '">' +
        model.frags.map(cardHTML).join('') + '</div></div>';
    }
    if (model.menus && model.menus.length) {
      html += '<div class="ojs-band">' +
        '<div><span class="ojs-label">' + esc(CFG.strings.waysToTry) + '</span>' +
        '<span class="ojs-label-note" style="display:block;margin-top:4px">' + esc(CFG.strings.waysToTryNote) + '</span></div>' +
        '<div class="ojs-cards-4">' + model.menus.map(cardHTML).join('') + '</div>' +
        (CFG.tenMlUrl ? '<a href="' + esc(CFG.tenMlUrl) + '" class="ojs-rowlink">' + esc(CFG.strings.everyIn10) + ' ' + arrow() + '</a>' : '') +
        '</div>';
    }
    if (model.homes.length) {
      html += '<div class="ojs-band"><span class="ojs-label">' + esc(CFG.strings.home) + '</span>' +
        '<div class="ojs-home-row">' + model.homes.map(homeCardHTML).join('') + '</div></div>';
    }
    html += alsoHTML(model.also);
    html += seeAllHTML(model);
    return html;
  }

  function formatHTML(model) {
    var html = '<div class="ojs-band">' +
      '<div><span class="ojs-label">' + esc(CFG.strings.waysToTry) + '</span>' +
      '<span class="ojs-label-note" style="display:block;margin-top:4px">' + esc(CFG.strings.waysToTryNote) + '</span></div>' +
      '<div class="ojs-cards-4">' + model.items.map(cardHTML).join('') + '</div>' +
      (CFG.tenMlUrl ? '<a href="' + esc(CFG.tenMlUrl) + '" class="ojs-rowlink">' + esc(CFG.strings.everyIn10) + ' ' + arrow() + '</a>' : '') +
      '</div>';
    html += seeAllHTML({ total: model.items.length, q: model.q });
    return html;
  }

  function render(model) {
    root.classList.remove('is-noresults');
    if (model.kind === 'concierge') {
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
      conciergeEl.hidden = false;
      root.classList.remove('is-typing');
      helperEl.textContent = '';
      renderRecents();
      return;
    }
    helperEl.textContent = CFG.strings.pressEnter;
    recentEl.hidden = true;

    if (model.kind === 'none') {
      resultsEl.innerHTML = '<span class="ojs-none-text">' + esc(fmt(CFG.strings.nothingFor, { q: model.q.trim() })) + '</span>';
      resultsEl.hidden = false;
      conciergeEl.hidden = false;
      root.classList.add('is-noresults');
      root.classList.remove('is-typing');
      return;
    }

    var html = '';
    if (model.kind === 'best') html = bestHTML(model);
    else if (model.kind === 'rows') html = rowsHTML(model);
    else if (model.kind === 'format') html = formatHTML(model);

    resultsEl.innerHTML = html;
    resultsEl.hidden = false;
    conciergeEl.hidden = true;
    root.classList.remove('is-typing');
  }

  /* ---------- state ---------- */

  function update() {
    var q = (input.value || '').trim();
    clearBtn.hidden = !q.length;

    if (norm(q) === norm(lastQuery)) return;
    lastQuery = q;

    if (q.length < (CFG.minChars || 1)) {
      renderCompletions([]);
      render({ kind: 'concierge' });
      return;
    }

    root.classList.add('is-typing');
    root.classList.remove('is-noresults');
    helperEl.textContent = CFG.strings.pressEnter;
    recentEl.hidden = true;
    renderCompletions(computeCompletions(q));

    clearTimeout(debounceTimer);
    if (q.length < 2) {
      /* first character: completions float over the dimmed concierge only */
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
      conciergeEl.hidden = false;
      return;
    }
    debounceTimer = setTimeout(function () {
      render(compute(q));
    }, 220);
  }

  function isOpen() {
    return root.classList.contains('open');
  }

  function openPanel() {
    loadIndex();
    root.classList.add('open');
    if (overlay) overlay.classList.add('open');
    var navSearch = document.getElementById('nav_search');
    if (navSearch) navSearch.classList.add('open');
    /* mobile hamburger state, mirrors the legacy popup */
    var mw = document.querySelector('.left-nav-mobile-wrapper');
    if (mw && window.matchMedia('(max-width: 991px)').matches) mw.classList.add('open-search');
    document.querySelectorAll('li.with-dropdown.open').forEach(function (li) { li.classList.remove('open'); });
    renderRecents();
    setTimeout(function () { input.focus(); }, 60);
  }

  function closePanel() {
    root.classList.remove('open', 'is-typing', 'is-noresults');
    if (overlay) overlay.classList.remove('open');
    var navSearch = document.getElementById('nav_search');
    if (navSearch) {
      navSearch.classList.remove('open');
      try { navSearch.focus(); } catch (e) { /* noop */ }
    }
    document.querySelectorAll('.open-search').forEach(function (el) { el.classList.remove('open-search'); });
    input.value = '';
    lastQuery = '';
    clearBtn.hidden = true;
    renderCompletions([]);
    render({ kind: 'concierge' });
  }

  function clearField() {
    input.value = '';
    lastQuery = '';
    update();
    input.focus();
  }

  function goFullResults(q) {
    addRecent(q);
    window.location.href = searchHref(q);
  }

  /* ---------- init ---------- */

  function init() {
    root = document.querySelector('.nav-search-dropdown.ojs');
    if (!root) return;

    var cfgEl = document.getElementById('ojs-config');
    try {
      CFG = JSON.parse(cfgEl.textContent) || {};
    } catch (e) {
      return;
    }
    /* The product index is fetched the first time the panel is about to open
       (see loadIndex); warm it on hover/touch of the search triggers. */
    document.querySelectorAll('.nav-search-li, .mobile-search').forEach(function (el) {
      ['mouseenter', 'touchstart', 'focusin'].forEach(function (evt) {
        el.addEventListener(evt, function () { loadIndex(); }, { once: true, passive: true });
      });
    });

    overlay = document.querySelector('.ojs-overlay');
    input = root.querySelector('.ojs-input');
    clearBtn = root.querySelector('.ojs-clear');
    helperEl = root.querySelector('.ojs-helper');
    recentEl = root.querySelector('.ojs-recent');
    listboxEl = root.querySelector('.ojs-completions');
    conciergeEl = root.querySelector('.ojs-concierge');
    resultsEl = root.querySelector('.ojs-results');

    /* triggers — same hooks the legacy popup used */
    document.addEventListener('click', function (ev) {
      var t = ev.target;
      var trigger = t.closest && (t.closest('.nav-search-li') || t.closest('.mobile-search'));
      if (trigger) {
        ev.preventDefault();
        if (isOpen()) closePanel(); else openPanel();
        return;
      }
      if (t.closest && (t.closest('.ojs-close') || t.closest('.ojs-close-m'))) {
        ev.preventDefault();
        closePanel();
        return;
      }
      if (overlay && (t === overlay)) {
        closePanel();
        return;
      }
      var chip = t.closest && t.closest('[data-ojs-recent-q]');
      if (chip) {
        input.value = chip.getAttribute('data-ojs-recent-q');
        lastQuery = '';
        update();
        input.focus();
        return;
      }
      if (t.closest && t.closest('[data-ojs-recent-clear]')) {
        try { localStorage.removeItem(RECENT_KEY); } catch (e) { /* noop */ }
        renderRecents();
        return;
      }
      var comp = t.closest && t.closest('[data-ojs-comp]');
      if (comp) {
        addRecent(input.value);
      }
      /* any result/product link inside the panel remembers the query */
      if (t.closest && t.closest('.ojs-results a')) {
        addRecent(input.value);
      }
    });

    input.addEventListener('input', update);

    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        moveHl(1);
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        moveHl(-1);
      } else if (ev.key === 'Enter') {
        ev.preventDefault();
        var q = (input.value || '').trim();
        if (!listboxEl.hidden && hlIndex >= 0 && currentComps[hlIndex]) {
          addRecent(q);
          window.location.href = currentComps[hlIndex].e.u;
        } else if (q.length) {
          goFullResults(q);
        }
      }
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape' || !isOpen()) return;
      if ((input.value || '').length) clearField();
      else closePanel();
    });

    clearBtn.addEventListener('click', function (ev) {
      ev.preventDefault();
      clearField();
    });

    render({ kind: 'concierge' });

    /* Compatibility shim: the navigation scripts still call the legacy popup API
       (window.search_popup.searchPopupClose) when the menu opens, so point it at this panel. */
    window.search_popup = window.search_popup || {};
    window.search_popup.searchPopupClose = closePanel;
    window.search_popup.searchPopupOpen = openPanel;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
